import { arrowBack, arrowForward } from "ionicons/icons";
import EncarteCard from "../EncarteCard.jsx";
import Loading from "../Loading.jsx";
import { useOutletContext } from "react-router";
import { IonIcon } from "@ionic/react";
import {
  Category,
  EmptyState,
  NavButton,
  SliderNav,
  SliderViewport,
} from "./ThemeListSliderStyles.js";
import { useEffect, useRef, useState } from "react";

/**
 * Component to display a slider of themes.
 * @param {number} pageSize - Number of themes per page
 * @param {string} title - Title of the slider
 * @param {*} fetchThemes - Function to fetch themes (page, size) => Promise<{content, last}>
 * @param {function} onThemeClick - Callback when a theme is clicked
 * @param {boolean} isAccountPremium - Whether the account is premium
 * @param {object} accountStatus - Account status object
 * @param {boolean} showPremiumTag - Whether to show premium tag on themes
 * @param {number} initialPage - Initial page number
 * @returns
 */

export default function ThemeListSlider({
  fetchThemes, // (page, size) => Promise<{content, last}>
  pageSize = 3,
  title,
  onThemeClick,
  isAccountPremium,
  accountStatus,
  showPremiumTag = true,
  initialPage = 0,
  titleAnimation = false,
  showWhenEmpty = false,
  emptyStateText,
  emptyStateCta,
}) {
  const [themes, setThemes] = useState([]);
  const [page, setPage] = useState(initialPage);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loading, setLoading] = useState(false);
  const { token, user } = useOutletContext();
  // page -> Promise<{resources, hasNextPage}>
  const cacheRef = useRef(new Map());

  useEffect(() => {
    cacheRef.current = new Map();
  }, [pageSize, fetchThemes, token, isAccountPremium, user?.email]);

  useEffect(() => {
    let mounted = true;
    const cache = cacheRef.current;

    const load = (p) => {
      if (!cache.has(p)) {
        const promise = fetchThemes(
          token,
          p,
          pageSize,
          isAccountPremium,
          user?.email,
        ).catch((err) => {
          cache.delete(p);
          throw err;
        });
        cache.set(p, promise);
      }
      return cache.get(p);
    };

    const current = load(page);
    // Only show the loader when the page isn't already available
    setLoading(true);
    current
      .then((data) => {
        if (!mounted) return;
        setThemes(data.resources || []);
        setHasNextPage(data.hasNextPage);
        setLoading(false);
        if (data.hasNextPage) load(page + 1).catch(() => {});
      })
      .catch(() => {
        if (mounted) setLoading(false);
      });

    // First render: fetch current and next page in parallel (6 themes)
    if (page === initialPage) load(page + 1).catch(() => {});

    return () => {
      mounted = false;
    };
  }, [page, pageSize, fetchThemes, token, isAccountPremium, user?.email]);

  const handleNext = () => setPage((p) => p + 1);
  const handlePrev = () => setPage((p) => Math.max(p - 1, 0));

  const isEmpty = themes?.length === 0;

  if (isEmpty && !loading && !showWhenEmpty) {
    return <></>;
  }

  return (
    <div>
      <Category titleAnimation={titleAnimation}>{title || "Temas"}</Category>
      {isEmpty && !loading ? (
        <EmptyState>
          {emptyStateText && <p>{emptyStateText}</p>}
          {emptyStateCta}
        </EmptyState>
      ) : (
        <SliderViewport>
          <div style={{ display: "flex", width: "100%" }}>
            {themes.map((theme, idx) => (
              <div
                key={theme.urlTmp || idx}
                style={{
                  width: "33.3333%",
                  flex: "0 0 33.3333%",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <EncarteCard
                  onClick={() => onThemeClick(theme)}
                  url={theme.urlTmp}
                  name={theme.name?.split(".")[0]}
                  isIncludedInPlan={
                    isAccountPremium || !theme.isPremiumResource
                  }
                  showPremiumTag={
                    showPremiumTag &&
                    ((!isAccountPremium && theme.isPremiumResource) ||
                      (theme.isPremiumResource && accountStatus?.onTrial))
                  }
                />
              </div>
            ))}
          </div>
        </SliderViewport>
      )}
      {!isEmpty && (
        <SliderNav>
          {page > 0 ? (
            <NavButton type="button" onClick={handlePrev}>
              <IonIcon icon={arrowBack} />
              Voltar
            </NavButton>
          ) : (
            <span />
          )}
          {hasNextPage && (
            <NavButton type="button" className="next" onClick={handleNext}>
              <IonIcon icon={arrowForward} />
              Carregar mais temas
            </NavButton>
          )}
        </SliderNav>
      )}
      {loading && (
        <Loading
          txt="Carregando temas..."
          $NoBg
          color="#fff"
          absolute={false}
        />
      )}
    </div>
  );
}
